import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { get, isEqual, isEmpty, orderBy } from 'lodash';
import classNames from 'classnames';
import { fetchGuestOrdersAction } from '../../actions/app.action';
import asyncRoute from '../../helpers/hoc/asyncRoute';
import { extractWhitelabelSubdomain, getTableLabel } from '../../utils/helpers';
import { getButtonHandlerCta, getErrorDescription, getErrorTitle } from '../../selectors';
import {
  apiResponseCodes,
  customErrorCodes,
  exceptionComponentPageTypes,
  orderStatusConfig,
  orderModes,
} from '../../constants/globals';
import { constants } from '../../constants/configuration';
import routes from '../../routes';
import './OrderList.scss';

const Header = asyncRoute(() => import('../../components/Header'));
const ExceptionComponent = asyncRoute(() => import('../../components/ExceptionComponent'));

class OrderList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      orderList: [],
      showErrorPopup: false,
      errorContent: {
        title: '',
        description: '',
        buttonCta: '',
      },
      handleErrorCTA: null,
      errorPopupCancellable: true,
      errorPopupStyleType: exceptionComponentPageTypes.halfSize,
      initialAPIInteractionDone: false, // Whether the API calls on mount have completed (irrespective of SUCCESS or ERROR response)
    };
    this.redirectToOrderDetail = this.redirectToOrderDetail.bind(this);
  }

  componentDidMount() {
    const {
      fetchGuestOrders,
      restaurant,
      loginData,
      isRestaurantEntityGroup,
      restaurantGroup: { restaurants },
    } = this.props;
    const guestId = get(loginData, 'id', '');

    // If guestId is empty due to contextless hit to orders route, show error
    if (isEmpty(guestId)) {
      this.setState({
        showErrorPopup: true,
        errorContent: {
          title: getErrorTitle({ code: customErrorCodes.contextlessURLHit }),
          description: getErrorDescription({ code: customErrorCodes.contextlessURLHit }),
          buttonCta: getButtonHandlerCta({ code: customErrorCodes.contextlessURLHit }),
        },
        handleErrorCTA: this.handleContextlessURLHitErrorPopupCTA,
        errorPopupStyleType: exceptionComponentPageTypes.fullSize,
        initialAPIInteractionDone: true, // No API to call in this scenario, so API interaction on mount is complete
      });
    } else {
      let restaurantIds = [];
      const pageSize = get(constants, 'orders.pageSize', 20);
      const sortType = get(constants, 'orders.sortType', 'orderPunchTime,DESC');
      const subDomain = extractWhitelabelSubdomain();
      if (subDomain) {
        if (typeof isRestaurantEntityGroup === 'boolean' && isRestaurantEntityGroup) {
          restaurantIds = !isEmpty(restaurants)
            ? restaurants.map((restaurantObj) => get(restaurantObj, 'id', ''))
            : [];
        } else {
          restaurantIds.push(get(restaurant, 'id', ''));
        }
      }

      let url = `/orders?guest=${guestId}&restaurant=${restaurantIds}&size=${pageSize}&sort=${sortType}`;

      fetchGuestOrders({ url }).then((response) => {
        // As the API call on mount has completed, mark the initialAPIInteractionDone as true
        this.setInitialAPIInteractionDone(true);

        // On API call success, populate orderList state with orderList prop
        if (apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
          const { orderList } = this.props;
          this.setState({
            orderList,
          });
        }
      });
    }

    // Scroll to top to prevent browser scroll position persistence from other screens
    window.scrollTo({ top: 0 });
  }

  handleContextlessURLHitErrorPopupCTA = () => {
    // Reload and open webapp's home
    window.location.replace(window.location.origin);
  };

  onCloseExceptionComponent = () => {
    this.setState({
      showErrorPopup: false,
      errorContent: {
        title: '',
        description: '',
        buttonCta: '',
      },
      handleErrorCTA: null,
    });
  };

  redirectToOrderDetail = (orderId) => {
    const { history, match } = this.props;

    if (!history || !match) return;

    history.push(`/${routes.containers.confirmation}/${orderId}`, {
      initiatorRoute: match.path,
    });
  };

  onBackClick = () => {
    const { history, parentRoute } = this.props;
    if (isEmpty(parentRoute)) {
      history.push('/'); // Take the guest back to top-level route which will further redirect to the contextual home
    } else {
      history.push(`${parentRoute}`);
    }
  };

  onViewMoreItemsClick = () => {
    const { links } = this.props;
    let url = get(links, 'next.href', '');
    this.props.fetchGuestOrders({ url }).then((response) => {
      if (apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
        const { orderList: nextOrderList } = this.props;
        const { orderList: prevOrderList } = this.state;
        const cumulativeOrderList = [...prevOrderList, ...nextOrderList]; // To append "view more" CTA results
        this.setState({
          orderList: cumulativeOrderList,
        });
      }
    });
  };

  setInitialAPIInteractionDone = (flag) => {
    this.setState({
      initialAPIInteractionDone: flag,
    });
  };

  render() {
    const {
      orderList,
      showErrorPopup,
      errorContent,
      handleErrorCTA,
      errorPopupCancellable,
      errorPopupStyleType,
      initialAPIInteractionDone,
    } = this.state;
    const { page } = this.props;
    const totalPages = get(page, 'totalPages', 0);
    const number = get(page, 'number', 0);

    const renderOrderStatus = (status, tableType) => {
      switch (status) {
        case orderStatusConfig.UNATTENDED:
          return constants.orders.orderStatus.pending;
        case orderStatusConfig.CONFIRMED:
          return constants.orders.orderStatus.confirmed;
        case orderStatusConfig.SETTLED:
          return constants.orders.orderStatus.delivered;
        case orderStatusConfig.FAILED:
          return constants.orders.orderStatus.failed;
        case orderStatusConfig.CANCELLED:
          return constants.orders.orderStatus.cancelled;
        case orderStatusConfig.READY:
          switch (tableType) {
            case orderModes.selfPickup:
            case orderModes.carOrder:
              return constants.orders.orderStatus.ready;
            case orderModes.delivery:
              return constants.orders.orderStatus.outForDelivery;
            default:
              return '';
          }
        default:
          return '';
      }
    };

    let orderState = (status) =>
      classNames({
        'order-status': true,
        unattended: status === 'UNATTENDED',
        confirmed: status === 'CONFIRMED',
        delivered: status === 'SETTLED',
        failed: status === 'FAILED',
        cancelled: status === 'CANCELLED',
        ready: status === 'READY',
      });

    return (
      <>
        {initialAPIInteractionDone && (
          <>
            <Header onBackClick={this.onBackClick} identifier={routes.containers.orders} />
            {orderList && orderList.length > 0 ? (
              <div className="order-list" id="order-list-wrapper">
                {orderBy(orderList, 'orderPunchTime', 'desc').map((order, index) => {
                  return (
                    <div
                      key={index}
                      className="order-list_tile"
                      onClick={() => this.redirectToOrderDetail(get(order, 'id'))}
                    >
                      {!isEqual(get(order, 'statusCode'), 'SETTLED') && (
                        <div className={orderState(get(order, 'statusCode'))}>
                          {renderOrderStatus(get(order, 'statusCode'), get(order, 'tableType'))}
                        </div>
                      )}
                      <div className="order-list_time">
                        {moment(get(order, 'orderPunchTime')).format('ll')}
                      </div>
                      <div className="order-list_restaurant">
                        <span className="restaurant-name">{get(order, 'restaurantName', '')}</span>
                        <i className="chevron right icon"></i>
                      </div>
                      <div className="order-list_amount-section">
                        <span>₹ {get(order, 'totalAmount')}</span>
                        <span>&nbsp;&nbsp; | &nbsp;&nbsp;</span>
                        <span>
                          {getTableLabel(
                            get(order, 'tableType'),
                            get(order, 'tableTitle', ''),
                            get(order, 'tableLabel'),
                          )}{' '}
                        </span>
                      </div>
                      {index < orderList.length - 1 && <div className="order-list_line-break" />}
                    </div>
                  );
                })}
                {number < totalPages && (
                  <div className="order-list_view-more-orders" onClick={this.onViewMoreItemsClick}>
                    {constants.orders.viewMoreOrders}
                  </div>
                )}
              </div>
            ) : (
              <div className="order-list_no-orders">
                <span className="text">{constants.orders.noOrder}</span>
                <span className="redirect-menu" onClick={this.onBackClick}>
                  {constants.orders.backToMenu}
                </span>
              </div>
            )}
          </>
        )}
        {showErrorPopup && (
          <ExceptionComponent
            pageType={errorPopupStyleType}
            openSegmentPopup={showErrorPopup}
            dismissSegmentPopup={errorPopupCancellable ? this.onCloseExceptionComponent : undefined} // Sending this prop as undefined as the SegmentComponent being used internally in ExceptionComponent implicitly handles it
            title={get(errorContent, 'title')}
            description={get(errorContent, 'description')}
            buttonCta={get(errorContent, 'buttonCta')}
            handleButtonCta={handleErrorCTA}
          />
        )}
      </>
    );
  }
}

export default connect(
  (state) => ({
    loginData: get(state, 'authentication.loginData', {}),
    page: get(state, 'app.guestOrders.page'),
    links: get(state, 'app.guestOrders._links'),
    orderList: get(state, 'app.guestOrders._embedded.orders', []),
    parentRoute: get(state, 'app.parentRoute', ''),
    restaurant: get(state, 'app.restaurant', {}),
    restaurantGroup: get(state, 'app.restaurantGroup', {}),
  }),
  {
    fetchGuestOrders: fetchGuestOrdersAction,
  },
)(OrderList);
